import type { ServiceSourceDocument } from '@ctw/shared/api/ai/use-extraction-jobs';
import type { MappedFactGroup } from '@ctw/shared/content/fact-extraction/map-extraction-job';
import type { ResolvedCitation } from '@ctw/shared/content/fact-extraction/use-citation-sources';

// Interim label for a service-resolved document. The export flow replaces it
// with the DocumentReference title once fetched.
const SERVICE_SOURCE_LABEL = 'Source document';

/**
 * The binaryId of a server-resolved source document, when the entry is usable.
 * Usable = a `verified` or `direct` status WITH a binaryId. `invalid`,
 * `indeterminate`, and document-less entries return undefined.
 */
export function getServiceResolvedBinaryId(
  entry: ServiceSourceDocument | undefined,
): string | undefined {
  if (entry && (entry.status === 'verified' || entry.status === 'direct')) {
    return entry.binaryId || undefined;
  }
  return undefined;
}

// Whether the service already supplied the document for this (value,
// citation), so the client has nothing left to resolve for it.
function isServiceSettled(entry: ServiceSourceDocument | undefined): boolean {
  return getServiceResolvedBinaryId(entry) !== undefined;
}

function findEntry(
  serviceSourceDocuments: Array<ServiceSourceDocument> | undefined,
  citationId: number,
): ServiceSourceDocument | undefined {
  return serviceSourceDocuments?.find((candidate) => candidate.citationId === citationId);
}

/**
 * Resolves the source for one (field value, citation) pair, preferring the
 * summarization service's verified document. Falls back to client-side
 * resolution (`clientResolvedByCitationId`) whenever the service supplied none: no
 * entry, an `indeterminate` or `invalid` entry, or a usable status without a
 * binaryId. An `invalid` verdict means the verifier found no candidate that
 * supports the value; the export still ships the client-resolved document so
 * the citation has something behind it. This is the single precedence point
 * for the export flow.
 */
export function resolveCitationForExport({
  citationId,
  serviceSourceDocuments,
  clientResolvedByCitationId,
}: {
  citationId: number;
  serviceSourceDocuments: Array<ServiceSourceDocument> | undefined;
  clientResolvedByCitationId: ReadonlyMap<number, ResolvedCitation>;
}): ResolvedCitation | undefined {
  const entry = findEntry(serviceSourceDocuments, citationId);
  const serviceBinaryId = getServiceResolvedBinaryId(entry);
  if (serviceBinaryId) {
    return { kind: 'document', binaryId: serviceBinaryId, label: SERVICE_SOURCE_LABEL };
  }
  return clientResolvedByCitationId.get(citationId);
}

export interface FactCitationDocument {
  citationId: number;
  binaryId: string;
  label: string;
}

/**
 * Resolves each of a fact's citations (service-first, then client-side) to its
 * source document, keeping only citations that resolve to a document. The
 * single iteration primitive shared by the export summary, the deliverability
 * classifier, and the manifest ref decoration so all three agree on which
 * binary backs each citation.
 */
export function resolveFactDocumentsForExport({
  citationIds,
  serviceSourceDocuments,
  clientResolvedByCitationId,
}: {
  citationIds: Array<number>;
  serviceSourceDocuments: Array<ServiceSourceDocument> | undefined;
  clientResolvedByCitationId: ReadonlyMap<number, ResolvedCitation>;
}): Array<FactCitationDocument> {
  const documents: Array<FactCitationDocument> = [];
  for (const citationId of citationIds) {
    const source = resolveCitationForExport({
      citationId,
      serviceSourceDocuments,
      clientResolvedByCitationId,
    });
    if (source?.kind === 'document') {
      documents.push({ citationId, binaryId: source.binaryId, label: source.label });
    }
  }
  return documents;
}

/**
 * The citations the service resolved to a document on every value that cites
 * them, for the export flow. Client-side document resolution MUST skip these.
 * A citation shared by several values is settled only if every one of them
 * carries a usable document for it; otherwise the client still resolves it for
 * the values the service left open.
 */
export function collectServiceResolvedCitationIds(
  groups: Array<MappedFactGroup>,
): ReadonlySet<number> {
  const openCitationIds = new Set<number>();
  const citedIds = new Set<number>();
  for (const group of groups) {
    for (const fact of group.facts) {
      for (const citationId of fact.citationIds) {
        citedIds.add(citationId);
        if (!isServiceSettled(findEntry(fact.serviceSourceDocuments, citationId))) {
          openCitationIds.add(citationId);
        }
      }
    }
  }
  return new Set([...citedIds].filter((citationId) => !openCitationIds.has(citationId)));
}
